/**
 * GroupCompletenessNotice — tells the owner when a group total is not the
 * whole group: a business whose report failed, or a business that reports in
 * another currency (kept out of the totals rather than added as if the
 * currencies were the same). Renders nothing when the total is complete.
 */
import { AlertTriangle } from "lucide-react";

export interface GroupReportMeta {
  currency?: string;
  incomplete?: boolean;
  failedBusinesses?: Array<{ businessId: string; businessName: string; reason?: string }>;
  otherCurrencyBusinesses?: Array<{ businessId: string; businessName: string; currency: string }>;
}

export default function GroupCompletenessNotice({ meta }: { meta?: GroupReportMeta | null }) {
  if (!meta?.incomplete) return null;
  const failed = meta.failedBusinesses || [];
  const other = meta.otherCurrencyBusinesses || [];
  return (
    <div role="status" className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
      <div className="space-y-1">
        <p className="font-medium">These totals don't include every business.</p>
        {failed.length > 0 && (
          <p>
            Couldn't load: {failed.map((b) => b.businessName).join(", ")}. Try again shortly; if it keeps happening,
            contact support.
          </p>
        )}
        {other.length > 0 && (
          <p>
            Reported in another currency, so not added to the {meta.currency} totals:{" "}
            {other.map((b) => `${b.businessName} (${b.currency})`).join(", ")}.
          </p>
        )}
      </div>
    </div>
  );
}
